import React, { useState } from "react";
import { resolveImageUrl } from "../../utils/imageStorage.js";
import { Check, Clock, Sparkles, ArrowRight } from "lucide-react";

export const CurriculumPage = ({
  curriculum,
  settings,
  lang,
  onNavigate,
  pageData
}) => {
  const [selectedClassId, setSelectedClassId] = useState(curriculum[0]?.id || "");
  const activeItem = curriculum.find((c) => c.id === selectedClassId) || curriculum[0];

  return (
    <div className="py-12 bg-stone-50 min-h-screen space-y-16">
      {/* Page Header */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-emerald-950/80 backdrop-blur-md text-white rounded-2xl p-8 sm:p-12 relative overflow-hidden shadow-sm">
          {pageData?.hero_image && (
            <div className="absolute inset-0 z-0">
              <img
                src={pageData.hero_image}
                alt="Curriculum Banner"
                className="w-full h-full object-cover opacity-25"
                referrerPolicy="no-referrer"
              />
              <div className="absolute inset-0 bg-emerald-950/65" />
            </div>
          )}
          <div className="max-w-3xl space-y-3 relative z-10">
            <span className="text-amber-400 font-bold text-xs uppercase tracking-widest block font-sans">
              {lang === "bn" ? pageData?.title_bn || "সমন্বিত কারিকুলাম ও একাডেমিক সিলেবাস" : pageData?.title || "Academic Curriculum & Programs"}
            </span>
            <h1 className="text-3xl sm:text-5xl font-serif font-bold tracking-tight text-balance">
              {lang === "bn" ? pageData?.subtitle_bn || "দ্বিমুখী সমন্বিত পাঠ্যক্রম" : pageData?.subtitle || "Dual-Track Excellence in Faith & Science"}
            </h1>
            <p className="text-sm sm:text-base text-emerald-100/90 leading-relaxed font-sans">
              {lang === "bn" ? pageData?.content_bn || "প্লে-নার্সারি থেকে ৫ম শ্রেণি এবং আন্তর্জাতিক হিফজুল কুরআন বিভাগ: বাংলা, আরবি, ইংরেজি, ফার্সি ও গণিতের অনন্য মেলবন্ধন।" : pageData?.content || "Comprehensive curriculum bridging sacred Quranic memorization with rigorous NCTB primary schooling, foreign language acquisition, and disciplined character formation."}
            </p>
          </div>
        </div>
      </div>

      {/* Interactive Class Selector & Detail View */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Class Navigation Tabs */}
          <div className="lg:col-span-4 space-y-2">
            <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider px-2 mb-3">
              {lang === "bn" ? "শ্রেণি ও বিভাগসমূহ নির্বাচন করুন" : "Select Program / Department"}
            </h3>
            <div className="space-y-2">
              {curriculum.map((item) => {
                const isSelected = item.id === activeItem?.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => setSelectedClassId(item.id)}
                    className={`w-full text-left p-4 rounded-xl border transition-all cursor-pointer flex items-center justify-between ${
                      isSelected
                        ? "bg-emerald-900 text-white border-emerald-900 shadow-md font-bold"
                        : "bg-white text-slate-800 border-stone-200 hover:border-emerald-600/40"
                    }`}
                  >
                    <div>
                      <span className="text-sm block">
                        {lang === "bn" ? item.class_name_bn : item.class_name}
                      </span>
                      <span
                        className={`text-xs block mt-0.5 ${isSelected ? "text-emerald-200" : "text-slate-500"}`}
                      >
                        {item.age_group} · {item.duration}
                      </span>
                    </div>
                    <ArrowRight
                      className={`w-4 h-4 shrink-0 transition-transform ${isSelected ? "translate-x-1 text-amber-300" : "text-slate-400"}`}
                    />
                  </button>
                );
              })}
            </div>

            {/* Quick download box */}
            <div className="mt-8 p-5 bg-amber-50/80 rounded-xl border border-amber-200/80 space-y-3">
              <span className="text-xs font-bold text-amber-900 block">
                {lang === "bn" ? "২০২৬ ভর্তি সিলেবাস ও বইয়ের তালিকা" : "2026 Syllabus & Booklist"}
              </span>
              <p className="text-xs text-amber-800 leading-relaxed">
                {lang === "bn" ? "প্লে থেকে ৫ম শ্রেণি পর্যন্ত বিস্তারিত বিষয়ভিত্তিক পাঠ্যতালিকা জানতে ভর্তি ডেস্কে যোগাযোগ করুন।" : "Contact our admission desk to request the full printable syllabus booklet and booklist."}
              </p>
              <button
                onClick={() => onNavigate("contact")}
                className="w-full py-2.5 px-3 text-xs font-bold text-emerald-950 bg-amber-400 hover:bg-amber-300 rounded-lg shadow-xs transition-colors cursor-pointer"
              >
                {lang === "bn" ? "সিলেবাস ও ভর্তির তথ্য চান" : "Inquire for Syllabus PDF"}
              </button>
            </div>
          </div>

          {/* Active Class Detail Display */}
          {activeItem && (
            <div className="lg:col-span-8 bg-white p-6 sm:p-10 rounded-2xl border border-stone-200 shadow-sm space-y-8">
              <div>
                <div className="flex flex-wrap items-center gap-3 text-xs text-emerald-800 font-semibold mb-2">
                  <span className="bg-emerald-50 px-2.5 py-1 rounded text-emerald-900">
                    {activeItem.age_group}
                  </span>
                  <span>·</span>
                  <span>{activeItem.duration}</span>
                  <span>·</span>
                  <span className="flex items-center gap-1 text-slate-500">
                    <Clock className="w-3.5 h-3.5" />
                    {activeItem.schedule}
                  </span>
                </div>

                <h2 className="text-2xl sm:text-3xl font-serif font-bold text-slate-900">
                  {lang === "bn" ? activeItem.class_name_bn : activeItem.class_name}
                </h2>

                <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                  {lang === "bn" ? activeItem.description_bn : activeItem.description}
                </p>
              </div>

              {/* Subjects breakdown */}
              <div>
                <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-4 border-b border-stone-200 pb-2">
                  {lang === "bn" ? "পাঠ্য বিষয়সমূহ (Subjects Taught)" : "Subjects & Coursework"}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {(lang === "bn" ? activeItem.subjects_bn : activeItem.subjects).map((subj, idx) => (
                    <div
                      key={idx}
                      className="p-3 rounded-lg bg-stone-50 border border-stone-200/80 flex items-center gap-2.5 text-xs text-slate-800"
                    >
                      <span className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-800 font-bold flex items-center justify-center text-[10px] shrink-0">
                        {idx + 1}
                      </span>
                      <span className="font-medium">{subj}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Unique Pedagogical Features */}
              <div>
                <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-4 border-b border-stone-200 pb-2">
                  {lang === "bn" ? "বিশেষ পাঠদান বৈশিষ্ট্য" : "Key Teaching Features"}
                </h3>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {activeItem.features.map((feat, idx) => (
                    <li key={idx} className="flex items-start gap-2.5 text-xs text-slate-700">
                      <Check className="w-4 h-4 text-emerald-700 shrink-0 mt-0.5" />
                      <span>{feat}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Special Note on Chalk-Slate & Cursive Handwriting */}
              <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-200/70 text-xs text-emerald-900 leading-relaxed flex items-start gap-3">
                <Sparkles className="w-5 h-5 text-emerald-800 shrink-0 mt-0.5" />
                <div>
                  <strong className="block font-bold mb-1">
                    {lang === "bn" ? "হাতে-কলমে স্লেট অনুশীলন ও ইংরেজি টানা হাতের লেখা" : "Hands-on Slate Calligraphy & Cursive Handwriting"}
                  </strong>
                  <span>
                    {lang === "bn" ? "আমাদের মাদ্রাসায় প্রতিটি শিক্ষার্থীকে চক-স্লেটের মাধ্যমে বিশুদ্ধ আরবি হরফ, তাজবীদ বাক্য এবং ইংরেজি টানা হাতের লেখা যত্নসহকারে রপ্ত করানো হয়। নিয়মিত পাঠ ডায়েরির মাধ্যমে শিক্ষকদের নিবিড় তদারকি বজায় থাকে।" : "Students practice fine Arabic calligraphy on traditional chalk slates and master flowing cursive script on blackboards, recorded in personalized daily study logbooks."}
                  </span>
                </div>
              </div>

              <div className="pt-2 flex justify-end">
                <button
                  onClick={() => onNavigate("contact")}
                  className="px-6 py-3 text-xs font-bold text-white bg-emerald-800 hover:bg-emerald-900 rounded-lg shadow-sm cursor-pointer"
                >
                  {lang === "bn" ? "এই বিভাগে ভর্তির আবেদন করুন" : "Apply for this Class"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Visual Curriculum Gallery */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white p-8 rounded-2xl border border-stone-200 shadow-xs space-y-6">
          <div className="max-w-2xl">
            <h3 className="text-xl font-serif font-bold text-slate-900">
              {lang === "bn" ? "শ্রেণিকক্ষ ও শিক্ষা উপকরণের ঝলক" : "Classroom Materials & Daily Learning"}
            </h3>
            <p className="text-xs sm:text-sm text-slate-600 mt-1">
              {lang === "bn" ? "আধুনিক চক-স্লেট, সহীহ তাজবীদ চার্ট এবং মার্জিত পড়াশোনার বাস্তব চিত্র" : "Authentic moments of student dedication, calligraphy practice, and structured daily study."}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            <div className="space-y-2">
              <div className="h-56 rounded-xl overflow-hidden border border-stone-200">
                <img
                  src={resolveImageUrl(
                    pageData?.photos?.find((p) => p.key === "classroom")?.url || "/assets/images/uploads/madrasah_campus_hero_1790844614157.jpg"
                  )}
                  alt="Classroom Study"
                  className="w-full h-full object-cover"
                  referrerPolicy="no-referrer"
                />
              </div>
              <p className="text-xs text-slate-700 font-medium">
                {lang === "bn" ? "মনোযোগ দিয়ে পাঠ্যবই অধ্যয়ন" : "Dedicated Textbook & Lesson Reading"}
              </p>
            </div>

            <div className="space-y-2">
              <div className="h-56 rounded-xl overflow-hidden border border-stone-200">
                <img
                  src={resolveImageUrl(
                    pageData?.photos?.find((p) => p.key === "books")?.url || pageData?.photos?.find((p) => p.key === "curriculum")?.url || "/assets/images/uploads/madrasah_campus_hero_1790844614157.jpg"
                  )}
                  alt="Curriculum Books"
                  className="w-full h-full object-cover"
                  referrerPolicy="no-referrer"
                />
              </div>
              <p className="text-xs text-slate-700 font-medium">
                {lang === "bn" ? "আরবি ক্যালোগ্রাফি ও সমন্বিত পাঠ্যবই" : "Calligraphy Slates & Integrated Textbooks"}
              </p>
            </div>

            <div className="space-y-2">
              <div className="h-56 rounded-xl overflow-hidden border border-stone-200">
                <img
                  src={resolveImageUrl(
                    pageData?.photos?.find((p) => p.key === "campus")?.url || pageData?.hero_image || "/assets/images/uploads/madrasah_campus_hero_1790844614157.jpg"
                  )}
                  alt="Campus Environment"
                  className="w-full h-full object-cover"
                  referrerPolicy="no-referrer"
                />
              </div>
              <p className="text-xs text-slate-700 font-medium">
                {lang === "bn" ? "সুপরিসর দোতলা ভবন ও খেলার মাঠ" : "Spacious Arched Campus & Natural Greenery"}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
